import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Settings, Save, Loader2, Download, Upload, Shield, AlertTriangle, Hotel, Plus, X, Star, StarOff } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { AboutHistoryCard } from './AboutHistoryCard';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const SettingsPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const fileInputRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/config`, { headers: getAuthHeaders() });
      setConfig(res.data);
    } catch {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/retreat/config`, config, { headers: getAuthHeaders() });
      setConfig(res.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/backup/export`, { headers: getAuthHeaders() });
      const json = JSON.stringify(res.data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rojo-retreat-backup-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const total = Object.values(res.data.summary || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup downloaded — ${total} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupBusy(false);
    }
  };

  const handleRestoreFile = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!window.confirm(
      `Restore from "${file.name}"?\n\nMerge mode will skip records that already exist (by ID or email). Click OK to proceed.`
    )) return;

    setRestoreBusy(true);
    try {
      const text = await file.text();
      const snapshot = JSON.parse(text);
      if (!snapshot.collections) throw new Error('Not a valid ROJO backup file');
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/backup/restore`,
        { snapshot, mode: 'merge' },
        { headers: getAuthHeaders() }
      );
      const totalNew = Object.values(res.data.results || {}).reduce((a, r) => a + (r.inserted || 0), 0);
      toast.success(`Restored — ${totalNew} new records added`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Restore failed');
    } finally {
      setRestoreBusy(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="settings-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Configure retreat details and pricing</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Retreat Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4 text-orange-600" />
              Retreat Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Retreat Name</Label>
              <Input value={config.retreat_name} onChange={(e) => setConfig({ ...config, retreat_name: e.target.value })} data-testid="config-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tagline</Label>
              <Input value={config.tagline} onChange={(e) => setConfig({ ...config, tagline: e.target.value })} data-testid="config-tagline" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Dates</Label>
                <Input value={config.dates} onChange={(e) => setConfig({ ...config, dates: e.target.value })} data-testid="config-dates" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Location</Label>
                <Input value={config.location} onChange={(e) => setConfig({ ...config, location: e.target.value })} data-testid="config-location" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Check-in</Label>
                <Input value={config.check_in} onChange={(e) => setConfig({ ...config, check_in: e.target.value })} data-testid="config-checkin" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Check-out</Label>
                <Input value={config.check_out} onChange={(e) => setConfig({ ...config, check_out: e.target.value })} data-testid="config-checkout" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Contact Email</Label>
              <Input value={config.contact_email} onChange={(e) => setConfig({ ...config, contact_email: e.target.value })} data-testid="config-email" />
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pricing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Base Price ($)</Label>
                <Input type="number" value={config.base_price} onChange={(e) => setConfig({ ...config, base_price: parseFloat(e.target.value) || 0 })} data-testid="config-price" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deposit Amount ($)</Label>
                <Input type="number" value={config.deposit_amount} onChange={(e) => setConfig({ ...config, deposit_amount: parseFloat(e.target.value) || 0 })} data-testid="config-deposit" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hotel Recommendations */}
        <Card data-testid="hotels-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Hotel className="w-4 h-4 text-gray-500" />
              Hotel Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500">
              Hotels shown on each attendee's portal. Brothers see this when planning their trip — keep it short and helpful.
            </p>
            {(config.hotels || []).map((hotel, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-white" data-testid={`hotel-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                      onClick={() => {
                        const next = [...config.hotels];
                        next[i] = { ...next[i], recommended: !next[i].recommended };
                        setConfig({ ...config, hotels: next });
                      }}
                      className={hotel.recommended ? 'text-orange-500' : 'text-gray-300 hover:text-gray-500'}
                      title={hotel.recommended ? 'Recommended — click to unstar' : 'Click to mark as recommended'}
                      data-testid={`hotel-recommended-${i}`}
                    >
                      {hotel.recommended ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                    </button>
                    <Input
                      value={hotel.name || ''}
                      onChange={(e) => {
                        const next = [...config.hotels];
                        next[i] = { ...next[i], name: e.target.value };
                        setConfig({ ...config, hotels: next });
                      }}
                      placeholder="Hotel name"
                      className="font-semibold"
                      data-testid={`hotel-name-${i}`}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setConfig({ ...config, hotels: config.hotels.filter((_, idx) => idx !== i) });
                    }}
                    className="text-gray-300 hover:text-red-500 shrink-0"
                    title="Remove hotel"
                    data-testid={`hotel-remove-${i}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    value={hotel.area || ''}
                    onChange={(e) => {
                      const next = [...config.hotels];
                      next[i] = { ...next[i], area: e.target.value };
                      setConfig({ ...config, hotels: next });
                    }}
                    placeholder="Area (e.g. St. Louis Park, Minneapolis)"
                    className="text-xs"
                  />
                  <Input
                    value={hotel.distance || ''}
                    onChange={(e) => {
                      const next = [...config.hotels];
                      next[i] = { ...next[i], distance: e.target.value };
                      setConfig({ ...config, hotels: next });
                    }}
                    placeholder="Distance (e.g. ~20 min from MSP)"
                    className="text-xs"
                  />
                </div>
                <Textarea
                  value={hotel.description || ''}
                  onChange={(e) => {
                    const next = [...config.hotels];
                    next[i] = { ...next[i], description: e.target.value };
                    setConfig({ ...config, hotels: next });
                  }}
                  placeholder="Why this hotel? Any tips for brothers staying here…"
                  rows={2}
                  className="text-xs resize-none"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    value={hotel.url || ''}
                    onChange={(e) => {
                      const next = [...config.hotels];
                      next[i] = { ...next[i], url: e.target.value };
                      setConfig({ ...config, hotels: next });
                    }}
                    placeholder="Booking / Google Maps URL"
                    className="text-xs"
                  />
                  <Input
                    value={hotel.phone || ''}
                    onChange={(e) => {
                      const next = [...config.hotels];
                      next[i] = { ...next[i], phone: e.target.value };
                      setConfig({ ...config, hotels: next });
                    }}
                    placeholder="Phone (optional)"
                    className="text-xs"
                  />
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setConfig({
                  ...config,
                  hotels: [...(config.hotels || []), {
                    name: '', area: '', distance: '', description: '', url: '', phone: '', recommended: false
                  }],
                });
              }}
              className="w-full border-dashed"
              data-testid="hotel-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add another hotel
            </Button>
          </CardContent>
        </Card>

        <AboutHistoryCard />

        {/* Backup & Restore */}
        <Card className="border-amber-200" data-testid="backup-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-600" />
              Database Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>Run a backup before any major change.</strong> Saves all attendees, rooms, emails, and config to a single JSON file you can restore anytime.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={handleBackup}
                disabled={backupBusy}
                variant="outline"
                className="justify-start"
                data-testid="backup-download-btn"
              >
                {backupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {backupBusy ? 'Preparing...' : 'Download Backup'}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleRestoreFile}
                className="hidden"
                data-testid="backup-restore-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={restoreBusy}
                variant="outline"
                className="justify-start"
                data-testid="backup-restore-btn"
              >
                {restoreBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {restoreBusy ? 'Restoring...' : 'Restore from Backup'}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Restore uses <strong>merge mode</strong> — records already in the database (matched by ID or email) are skipped, so your current data is never overwritten.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
